import { ENDPOINTS } from 'features/command-center/constants';
import { buildEndpointWithQueryParams, capitalizeName } from 'features/command-center/utils';
import startCase from 'lodash/startCase';
import moment from 'moment';
import { hasSingleRole } from 'services/Authorization';
import { DateFormats } from 'UI/constants/defaults';
import { Roles } from 'UI/constants/roles';
import { convertMinutesToSeconds, roundDecimals } from 'UI/utils';

import {
  ACTIVE_METRICS,
  EMPLOYEE_STATUS_METRICS,
  FEEDBACK_ACTIVITIES_METRICS,
  FEEDBACK_NOTE_METRICS,
  GOAL_METRICS,
  METRICS,
  TENURE_RANGES
} from './OverallPerformance.constants';

export const formatResponse = response => {
  const formattedData = response.data.data.map(item => ({
    ...item,
    recruiterName: {
      empNo: item.empNo,
      name: capitalizeName(item?.name)
    },
    team: {
      managerEmpNo: item.managerEmpNo,
      team: capitalizeName(item?.team)
    },
    inMails: {
      inMails: item.inMails,
      inMailsGoals: item.inMailsGoals
    },
    avgInMails: {
      avgInMails: item.avgInMails,
      avgInMailsGoals: item.avgInMailsGoals
    },
    bulks: {
      bulks: item.bulks,
      bulksGoal: item.bulksGoal
    },
    callsTime: {
      callsTime: item?.callsTime ? convertMinutesToSeconds(item?.callsTime) : 0,
      callsTimeGoal: item?.callsTimeGoal ? convertMinutesToSeconds(item.callsTimeGoal) : 0
    },
    calls: {
      calls: item.calls,
      callsGoal: item.callsGoal
    },
    peopleReached: {
      peopleReached: item.peopleReached,
      peopleReachedGoal: item.peopleReachedGoal
    },
    posts: {
      todayLivePosts: item.todayLivePosts,
      livePostingsDailyGoal: item.livePostingsDailyGoal
    },
    sendouts: {
      sendouts: item.sendouts,
      sendoutsGoal: item.sendoutsGoal
    },
    productionYTD: item.productionYtd,
    cashInYTD: item.cashInYtd,
    outStandingBillings: item.outstandingBillings,
    hireDate: item?.hireDate && moment.parseZone(item?.hireDate).format(DateFormats.SimpleDate),
    termDate: item?.termDate && moment.parseZone(item?.termDate).format(DateFormats.SimpleDate),
    tenure: item?.tenure ? TENURE_RANGES[item.tenure] : null,
    lastFeedbackActivityType:
      item?.lastFeedbackActivityType && startCase(item?.lastFeedbackActivityType),
    avgFeePercentage: item?.avgFeePercentage && roundDecimals(item?.avgFeePercentage, 2)
  }));

  return { ...response, data: { ...response.data, data: formattedData } };
};

export const getFinalMetrics = () =>
  [
    ...METRICS,
    ...GOAL_METRICS,
    ...FEEDBACK_NOTE_METRICS,
    ...EMPLOYEE_STATUS_METRICS,
    ...FEEDBACK_ACTIVITIES_METRICS,
    ...ACTIVE_METRICS
  ].join(',');

export const getReportFileName = period => {
  const { startDate, endDate } = period ?? {};
  const today = moment().format(DateFormats.QueryShortFormat);

  const fileStartDate = startDate ? moment(startDate).format(DateFormats.QueryShortFormat) : today;
  const fileEndDate = endDate ? moment(endDate).format(DateFormats.QueryShortFormat) : today;

  return `overall-performance-report_${fileStartDate}_${fileEndDate}_${Date.now()}`;
};

export const getReportMetrics = excludedMetrics => {
  const shouldIncludeFeedbackActivityMetrics = !hasSingleRole(Roles.Recruiter);

  return [
    ...METRICS,
    ...EMPLOYEE_STATUS_METRICS,
    ...(shouldIncludeFeedbackActivityMetrics ? FEEDBACK_ACTIVITIES_METRICS : []),
    ...ACTIVE_METRICS,
    ...FEEDBACK_NOTE_METRICS
  ].filter(metric => !excludedMetrics.includes(metric));
};

export const getDownloadEndpoint = ({ reportMetrics, queryParams }) => {
  const baseEndpoint = ENDPOINTS.overallPerformanceDownloadV2;

  return buildEndpointWithQueryParams({
    baseEndpoint,
    queryParams: queryParams.params,
    metrics: reportMetrics.join(',')
  });
};
